import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ClientBookMode, Location, Role, Service, Staff } from '@salonomy/types';
import { Button, Card, Field, Pill, TextInput } from '@salonomy/ui';
import { useLocations } from '../../data/locations.js';
import { useAnyWorkingHours } from '../../data/onboarding.js';
import { useSalon, useStaff, useUpdateSalon } from '../../data/salon.js';
import { useServices } from '../../data/services.js';
import { useAuth } from '../../lib/auth.js';
import { canEditStaffRole, ROLE_LABEL } from '../../lib/rbac.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { LocationsPanel } from '../locations/LocationsPanel.js';
import { RotaEditor } from '../scheduling/RotaEditor.js';
import { ServiceModal } from '../services/ServiceModal.js';
import { StaffModal } from '../team/StaffModal.js';
import { GoLiveChecklist } from './GoLiveChecklist.js';
import { canGoLive, computeSteps, outstandingRequired, STEP_ORDER, type StepId } from './goLive.js';
import { useWizardProgress } from './progress.js';
import './onboarding.css';

/** S1 — the client-book choice. Same copy/markup as the SettingsPage book-mode card (one source). */
const BOOK_MODES: Array<{ value: ClientBookMode; label: string; blurb: string }> = [
  { value: 'shared', label: 'One shared book', blurb: 'Every client is visible salon-wide.' },
  {
    value: 'sealed',
    label: 'Sealed per renter',
    blurb: 'Each chair renter’s clients are theirs alone — invisible to everyone else.',
  },
  {
    value: 'mixed',
    label: 'Mixed',
    blurb: 'Set per person — shared for employed staff, sealed for renters.',
  },
];

const STEP_TITLE: Record<StepId, string> = {
  basics: 'Salon basics',
  bookMode: 'Client book',
  locations: 'Locations',
  team: 'Your team',
  services: 'Services & prices',
  hours: 'Working hours',
  queueSlug: 'Your queue link',
};

const STEP_LEAD: Record<StepId, string> = {
  basics: 'Name your salon. This is what clients see everywhere.',
  bookMode: 'Choose how client records are shared across the team. You can change this later.',
  locations: 'Add at least one site, then assign your team to where they work.',
  team: 'Add the people who take bookings — and anyone who manages the salon.',
  services: 'Add your treatments with a starting price and how long each takes.',
  hours: 'Set the weekly hours that drive your diary and availability.',
  queueSlug: 'Pick the public link for your live walk-in queue.',
};

/**
 * WS6 brief item 7 — the first-run, RESUMABLE onboarding wizard. It does NOT reimplement any screen:
 * it COMPOSES the real ones (book-mode card, LocationsPanel, StaffModal, ServiceModal, RotaEditor) in
 * a guided frame, each step writing straight through to the API so the salon record is always the
 * source of truth. The cursor is persisted (features/onboarding/progress.ts) so closing the tab
 * resumes where you left off. The final step is the go-live checklist — the WS3 funnel's landing
 * target — which reads REAL salon state to show what's still missing.
 *
 * RBAC mirrors the API exactly: only an owner/manager can provision a salon. A non-manager sees an
 * honest, disabled-state explanation rather than a half-working wizard that would 403 on every write.
 */
export function OnboardingWizard() {
  const { isManager, user } = useAuth();
  const { data: salon } = useSalon();
  const { data: staff } = useStaff();
  const { data: services } = useServices();
  const { data: locations } = useLocations();
  const { anyWorkingHours } = useAnyWorkingHours(staff);
  const navigate = useNavigate();

  const progress = useWizardProgress(salon?.id);

  const steps = useMemo(
    () => computeSteps({ salon, staff, services, locations, anyWorkingHours }),
    [salon, staff, services, locations, anyWorkingHours],
  );
  const live = canGoLive(steps);
  const remaining = outstandingRequired(steps);

  // "Show me the checklist" is a distinct view from the step bodies — the landing target. We default
  // to it once everything is done so a returning, fully-set-up salon lands on the celebratory summary.
  const [view, setView] = useState<'steps' | 'checklist'>('steps');
  const [seededChecklist, setSeededChecklist] = useState(false);
  useEffect(() => {
    if (!seededChecklist && live) {
      setView('checklist');
      setSeededChecklist(true);
    }
  }, [live, seededChecklist]);

  if (!isManager) {
    return (
      <WizardShell>
        <Card>
          <div className="section-title">Set up your salon</div>
          <div className="muted" style={{ fontSize: 13.5, marginTop: 8, lineHeight: 1.5 }}>
            Only an owner or manager can run the setup wizard — it creates your team, services,
            hours and queue link. Ask whoever owns the salon account to finish setup, then you’ll
            see your own diary and profile.
          </div>
          <div style={{ marginTop: 16 }}>
            <Button variant="outline" onClick={() => void navigate({ to: '/' })}>
              Go to the diary
            </Button>
          </div>
        </Card>
      </WizardShell>
    );
  }

  const current = progress.current;
  const currentIndex = STEP_ORDER.indexOf(current);
  const isLastStep = currentIndex === STEP_ORDER.length - 1;
  const slug = salon?.slug?.trim() ?? '';

  function jumpToStep(step: StepId) {
    progress.goTo(step);
    setView('steps');
  }

  return (
    <WizardShell
      right={
        <Pill tone={live ? 'success' : 'cobalt'} className="mono">
          {live ? 'Ready to go live' : `${remaining} left`}
        </Pill>
      }
    >
      <div className="onboarding">
        {/* Step rail / timeline — a manager can jump to any step, and the checklist, at will. */}
        <nav className="onboarding__rail" aria-label="Setup steps">
          {STEP_ORDER.map((id, i) => {
            const s = steps.find((x) => x.id === id)!;
            const active = view === 'steps' && id === current;
            return (
              <button
                key={id}
                type="button"
                className={`onboarding__step${active ? ' onboarding__step--active' : ''}`}
                aria-current={active ? 'step' : undefined}
                onClick={() => jumpToStep(id)}
              >
                <span
                  className={`onboarding__bullet${s.done ? ' onboarding__bullet--done' : ''}`}
                  aria-hidden
                >
                  {s.done ? '✓' : i + 1}
                </span>
                <span className="onboarding__step-label">{STEP_TITLE[id]}</span>
              </button>
            );
          })}
          <button
            type="button"
            className={`onboarding__step${view === 'checklist' ? ' onboarding__step--active' : ''}`}
            aria-current={view === 'checklist' ? 'step' : undefined}
            onClick={() => setView('checklist')}
          >
            <span className="onboarding__bullet" aria-hidden>
              ★
            </span>
            <span className="onboarding__step-label">Go-live checklist</span>
          </button>
        </nav>

        <div className="onboarding__body">
          {view === 'checklist' ? (
            <>
              <GoLiveChecklist steps={steps} slug={slug} onJump={jumpToStep} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Button onClick={() => void navigate({ to: '/' })}>
                  {live ? 'Start using the diary' : 'Go to the diary for now'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="onboarding__head">
                <div className="onboarding__kicker mono">
                  Step {currentIndex + 1} of {STEP_ORDER.length}
                </div>
                <div className="section-title" style={{ fontSize: 22 }}>
                  {STEP_TITLE[current]}
                </div>
                <div className="muted" style={{ fontSize: 13.5, marginTop: 4 }}>
                  {STEP_LEAD[current]}
                </div>
              </div>

              <StepBody
                step={current}
                staff={staff ?? []}
                services={services ?? []}
                locations={locations ?? []}
                salonSlug={slug}
                clientBookMode={salon?.clientBookMode ?? 'shared'}
                tz={salon?.timezone ?? 'Europe/London'}
                selfStaffId={user?.staffId ?? null}
                actorRole={user?.role ?? 'receptionist'}
              />

              <div className="onboarding__nav">
                <Button variant="outline" onClick={progress.back} disabled={currentIndex === 0}>
                  Back
                </Button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="ghost" onClick={() => setView('checklist')}>
                    Skip to checklist
                  </Button>
                  {isLastStep ? (
                    <Button onClick={() => setView('checklist')}>Review checklist</Button>
                  ) : (
                    <Button onClick={progress.next}>Next</Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </WizardShell>
  );
}

function WizardShell({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <>
      <div className="topbar">
        <div className="topbar__title">Set up your salon</div>
        {right ? <div className="topbar__right">{right}</div> : null}
      </div>
      <div className="screen-scroll">
        <div
          className="screen-pad"
          style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 980 }}
        >
          {children}
        </div>
      </div>
    </>
  );
}

/** The body for the active step. The composed steps mount the REAL panels/modals, never copies. */
function StepBody({
  step,
  staff,
  services,
  locations,
  salonSlug,
  clientBookMode,
  tz,
  selfStaffId,
  actorRole,
}: {
  step: StepId;
  staff: Staff[];
  services: Service[];
  locations: Location[];
  salonSlug: string;
  clientBookMode: ClientBookMode;
  tz: string;
  selfStaffId: string | null;
  actorRole: Role;
}) {
  switch (step) {
    case 'basics':
      return <BasicsStep />;
    case 'bookMode':
      return <BookModeStep />;
    case 'locations':
      return <LocationsPanel locations={locations} staff={staff} />;
    case 'team':
      return (
        <TeamStep
          staff={staff}
          locations={locations}
          clientBookMode={clientBookMode}
          actorRole={actorRole}
        />
      );
    case 'services':
      return <ServicesStep services={services} />;
    case 'hours':
      return <RotaEditor staff={staff} tz={tz} selfStaffId={selfStaffId} />;
    case 'queueSlug':
      return <QueueSlugStep salonSlug={salonSlug} />;
    default:
      return null;
  }
}

// ── Salon-config steps (basics / book mode / queue slug) — write straight through useUpdateSalon ──

function BasicsStep() {
  const { data: salon } = useSalon();
  const update = useUpdateSalon();
  const toast = useToast();
  const [name, setName] = useState('');
  useEffect(() => {
    if (salon) setName(salon.name);
  }, [salon]);

  async function save() {
    if (!name.trim()) {
      toast.error('Give your salon a name.');
      return;
    }
    try {
      await update.mutateAsync({ name: name.trim() });
      toast.success('Salon name saved');
    } catch {
      toast.error('Could not save the salon name.');
    }
  }

  return (
    <Card>
      <Field label="Salon name" hint="Shown to clients on bookings, the queue and receipts.">
        <TextInput
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Shear Genius"
        />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <Button onClick={save} disabled={update.isPending || name.trim() === (salon?.name ?? '')}>
          Save name
        </Button>
      </div>
    </Card>
  );
}

function BookModeStep() {
  const { data: salon } = useSalon();
  const update = useUpdateSalon();
  const toast = useToast();
  const [mode, setMode] = useState<ClientBookMode>('shared');
  useEffect(() => {
    if (salon) setMode(salon.clientBookMode);
  }, [salon]);

  async function save(next: ClientBookMode) {
    setMode(next);
    try {
      await update.mutateAsync({ clientBookMode: next });
      toast.success('Client book mode saved');
    } catch {
      toast.error('Could not save the client book mode.');
    }
  }

  return (
    <Card>
      <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
        Sealed isolation is enforced by database row-level security, not a feature flag (ADR-0005).
        Pick what fits your salon — you can change it later.
      </div>
      <div className="comms-policies">
        {BOOK_MODES.map((m) => (
          <button
            key={m.value}
            className={`policy${mode === m.value ? ' policy--active' : ''}`}
            onClick={() => void save(m.value)}
          >
            <div className="policy__title">
              {m.label}
              {mode === m.value ? <Pill tone="cobalt">Selected</Pill> : null}
            </div>
            <div className="policy__blurb">{m.blurb}</div>
          </button>
        ))}
      </div>
    </Card>
  );
}

function QueueSlugStep({ salonSlug }: { salonSlug: string }) {
  const { data: salon } = useSalon();
  const update = useUpdateSalon();
  const toast = useToast();
  const [slug, setSlug] = useState(salonSlug);
  useEffect(() => {
    if (salon) setSlug(salon.slug);
  }, [salon]);

  async function save() {
    const next = slug.trim();
    if (!next) {
      toast.error('Pick a queue link.');
      return;
    }
    try {
      await update.mutateAsync({ slug: next });
      toast.success('Queue link saved');
    } catch {
      toast.error('That link is taken or invalid — try another.');
    }
  }

  return (
    <Card>
      <Field
        label="Queue link"
        hint={`Your public walk-in queue lives at salonomy.com/q/${slug.trim() || '…'}`}
      >
        <TextInput
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="shear-genius"
        />
      </Field>
      <div
        className="muted"
        style={{
          fontSize: 13,
          background: 'var(--slm-porcelain)',
          border: '1px solid var(--slm-border)',
          borderRadius: 8,
          padding: '10px 12px',
          margin: '4px 0 12px',
        }}
      >
        Clients open this link to take a ticket and watch their place and ETA from their phone — the
        live walk-in queue. Lowercase letters, numbers and hyphens.
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={save} disabled={update.isPending || slug.trim() === (salon?.slug ?? '')}>
          Save queue link
        </Button>
      </div>
    </Card>
  );
}

// ── Composed CRUD steps (team / services) — list + the REAL modal, no reimplementation ──

function TeamStep({
  staff,
  locations,
  clientBookMode,
  actorRole,
}: {
  staff: Staff[];
  locations: Location[];
  clientBookMode: ClientBookMode;
  actorRole: Role;
}) {
  const [modal, setModal] = useState<{ existing?: Staff } | null>(null);
  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="section-title">Team</div>
        <Button size="sm" onClick={() => setModal({})}>
          Add team member
        </Button>
      </div>
      {staff.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0' }}>
          No one on the team yet. Add your first stylist, barber or chair renter to start filling
          the diary.
        </div>
      ) : (
        <div className="settings-list">
          {staff.map((s) => {
            const canEdit = canEditStaffRole(actorRole, s.role);
            return (
              <div key={s.id} className="settings-row">
                <div>
                  <div style={{ fontWeight: 600 }}>{s.displayName}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {s.title ?? ROLE_LABEL[s.role] ?? s.role}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Pill tone="neutral">{ROLE_LABEL[s.role] ?? s.role}</Pill>
                  {s.renterId ? <Pill tone="soft">Sealed book</Pill> : null}
                  {!s.active ? <Pill tone="danger">Inactive</Pill> : null}
                  {canEdit ? (
                    <Button size="sm" variant="ghost" onClick={() => setModal({ existing: s })}>
                      Edit
                    </Button>
                  ) : (
                    <span
                      className="muted"
                      style={{ fontSize: 11.5 }}
                      title="Only the owner can edit the owner record"
                    >
                      Owner-only
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {modal ? (
        <StaffModal
          existing={modal.existing}
          locations={locations}
          clientBookMode={clientBookMode}
          onClose={() => setModal(null)}
        />
      ) : null}
    </Card>
  );
}

function ServicesStep({ services }: { services: Service[] }) {
  const [modal, setModal] = useState<{ existing?: Service } | null>(null);
  const categories = useMemo(
    () =>
      Array.from(
        new Set(services.map((s) => s.category).filter((c): c is string => Boolean(c))),
      ).sort(),
    [services],
  );
  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="section-title">Services</div>
        <Button size="sm" onClick={() => setModal({})}>
          Add service
        </Button>
      </div>
      {services.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0' }}>
          No services yet. Add your first treatment — name, duration and a starting price.
        </div>
      ) : (
        <div className="settings-list">
          {services.map((s) => (
            <div key={s.id} className="settings-row">
              <div>
                <div style={{ fontWeight: 600 }}>
                  {s.name}
                  {!s.active ? (
                    <Pill tone="danger" className="mono">
                      Archived
                    </Pill>
                  ) : null}
                </div>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  {s.durationMin} min{s.category ? ` · ${s.category}` : ''}
                  {s.requiresPatchTest ? ' · patch test' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className="mono">{money(s.priceFrom)}</span>
                <Button size="sm" variant="ghost" onClick={() => setModal({ existing: s })}>
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal ? (
        <ServiceModal
          existing={modal.existing}
          categories={categories}
          onClose={() => setModal(null)}
        />
      ) : null}
    </Card>
  );
}
